import { BoxContainer, BoxContainerProps, CircleIcon, FluidCanvas, MinimapCropMinimap, MinimapCropPan, MinimapCropProvider, MinimapCropViewport, PrimaryButton, ReactIcons, useMinimapCropContext, VideoControlsOverlay } from "@kalyzee/kalyzee-ui";

import workBoxStyles from "./WorkBox.module.css";
import styles from "./PlayerWorkBox.module.css";
import React from "react";
import clsx from "clsx";
import { Touchable, UserAvatar } from "@kalyzee/kast-web-sdk/web-components";
import { LayoutType } from "../../interfaces/ui";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { useSettings } from "../../hooks/settings";

/**
 * Round action button — used both in the topbar (always visible) and as a
 * centered overlay on hover. Same shape, different render slot.
 */
export interface PlayerActionItem {
  icon: React.ReactNode;
  onClick: () => void;
  label?: string;
  /** Optional key to stabilize React reconciliation (defaults to index). */
  key?: string;
}

/** @deprecated alias kept for compat — prefer {@link PlayerActionItem}. */
export type PlayerTopbarAction = PlayerActionItem;

/**
 * PTZ camera controls — directional pad + zoom buttons rendered as a full
 * overlay over the player. Each callback receives `true` on press-in and
 * `false` on press-out so the consumer can implement hold-to-move.
 */
export interface PlayerWorkBoxControlsOverlay {
  type: "controls";
  onRight?: (on: boolean) => void;
  onLeft?: (on: boolean) => void;
  onUp?: (on: boolean) => void;
  onDown?: (on: boolean) => void;
  onZoomIn?: (on: boolean) => void;
  onZoomOut?: (on: boolean) => void;
}

/**
 * Click-anywhere selection overlay — full-area dark overlay with a centered
 * directional icon. Use this when you want the entire player area to act as
 * a focus target, instead of (or in addition to) a small action button.
 */
export interface PlayerWorkBoxSelectionOverlay {
  type: "selection";
  onSelect: () => void;
  /** Drives the directional icon (down/up/left/right). */
  layout: LayoutType;
}

export type PlayerWorkBoxOverlayContent =
  | React.ReactNode
  | PlayerWorkBoxControlsOverlay
  | PlayerWorkBoxSelectionOverlay;

/**
 * Interactive minimap/crop config. When `enabled`, the player can be shown
 * cropped to a navigable viewport with an overlay minimap, toggled via an
 * overlay button — PlayerWorkBox owns the whole feature.
 */
export interface PlayerWorkBoxMinimap {
  enabled?: boolean;
  /** Content aspect ratio (width / height) for the crop. */
  ratio?: number;
  /**
   * Alternative minimap content (defaults to the player itself). Needed when
   * the player cannot be mounted twice — e.g. the VOD <video> elements are
   * ref-controlled by MultipleVideoPlayer, so only the viewport may render them.
   */
  minimapContent?: React.ReactNode;
  /** Controlled crop pan: a value enables crop mode (and is the scroll position);
   *  `undefined` means crop mode is off (whole frame shown). */
  pan?: MinimapCropPan;
  /** Update the pan, or pass `undefined` to disable crop mode. */
  onPanChange?: (pan: MinimapCropPan | undefined) => void;
  /**
   * Where the toggle + minimap stack anchors. `top` inverts the stack (minimap
   * above, exit toggle below) for hosts whose bottom edge is busy (e.g. the
   * VOD timeline bar). Defaults to `bottom`.
   */
  position?: "bottom" | "top";
}

const isTypedOverlay = (value: unknown): value is PlayerWorkBoxControlsOverlay | PlayerWorkBoxSelectionOverlay =>
  !!value && typeof value === "object" && "type" in (value as object);

/**
 * Crop-mode overlay: the "show whole image" toggle + the minimap. Rendered
 * inside the MinimapCrop context so it can read `canPan` — when the content
 * already fits the viewport (nothing to pan), both are hidden.
 */
const MinimapCropControls = ({ onExit, inverted }: { onExit: () => void; inverted?: boolean }) => {
  // const { canPan } = useMinimapCropContext();
  // if (!canPan) return null;
  return (
    <div className={clsx(styles.minimapOverlay, inverted && styles.minimapOverlayInverted)}>
      <PrimaryButton
        onClick={onExit}
        nativeProps={{ "aria-label": "Afficher toute l'image", title: "Afficher toute l'image" }}
      >
        <CircleIcon.Variants.UltraWide active className={styles.minimapToggleCircle} />
      </PrimaryButton>
      <div className={styles.minimapBox}>
        <MinimapCropMinimap minimapOpacity={1} />
      </div>
    </div>
  );
};

const focusIconForLayout = (layout: LayoutType) => {
  switch (layout) {
    case LayoutType.Top: return ReactIcons.SelectMainDown;
    case LayoutType.Bottom: return ReactIcons.SelectMainVertical;
    case LayoutType.Left: return ReactIcons.SelectMainRight;
    case LayoutType.Right: return ReactIcons.SelectMainHorizontal;
    default: return ReactIcons.Expand;
  }
};

export interface PlayerWorkBoxProps {
  children: React.ReactNode;
  label: string;
  labelInfo?: string;
  userDisplayName?: string;
  avatar?: string;
  overlayMode?: BoxContainerProps["mode"];
  /**
   * Free-form overlay content (e.g. PTZ controls). Renders inside the
   * topbar's overlay layer below the topbar itself.
   */
  overlayContent?: PlayerWorkBoxOverlayContent;
  /**
   * Round action buttons appended to the topbar's right slot, after the
   * existing camera/mic media buttons.
   */
  topbarActions?: PlayerActionItem[];
  extraHeaderContent?: React.ReactNode;
  isScreenSource?: boolean;
  /** Non-selected secondary (strip) player: hides the source info indicators. */
  isSidePlayer?: boolean;
  /** Interactive minimap/crop feature (owned entirely by PlayerWorkBox). */
  minimap?: PlayerWorkBoxMinimap;
  className?: string;
  topBarClassName?: string;
  audio?: boolean;
  video?: boolean;
  isMe?: boolean;
  isHighlighted?: boolean;
  isPlayingSound?: boolean;
  isSmall?: boolean;
  boxProps?: React.HTMLAttributes<HTMLDivElement>;
  containerRef?: React.RefObject<HTMLDivElement>;
  idleAnimation?: boolean;
  loading?: boolean;
  broken?: boolean;
  mediaButtons?: boolean;
  localStream?: boolean;
  onChangeAudio?: () => void;
  onChangeVideo?: () => void;
}

export const PlayerWorkBox = React.memo(({
  children,
  label,
  labelInfo,
  userDisplayName,
  avatar,
  overlayMode,
  overlayContent,
  topbarActions,
  extraHeaderContent,
  isScreenSource,
  isSidePlayer,
  minimap,
  className,
  topBarClassName,
  audio,
  video,
  isMe,
  isHighlighted,
  isPlayingSound,
  isSmall,
  boxProps,
  containerRef,
  idleAnimation,
  loading,
  broken,
  mediaButtons,
  localStream,
  onChangeAudio,
  onChangeVideo,
}: PlayerWorkBoxProps) => {
  const isMobile = useAppStoreSelector(state => state.isMobile);
  const [settings] = useSettings();

  const left = React.useMemo(() => {
    const result: React.ReactNode[] = [];
    if (extraHeaderContent) {
      result.push(extraHeaderContent);
    }
    if (isHighlighted) {
      result.push(<ReactIcons.Hand2 className={styles.nameIcon} />);
    } else if (isMe) {
      result.push(<ReactIcons.Home className={styles.nameIcon} />);
    }
    result.push(<span key="label" className={styles.userLabel}>{label}</span>);
    // labelInfo (device name) and the screen badge are dropped on mobile —
    // the topbar gets cramped quickly next to the username and the media
    // buttons. The label alone (user name) is enough to identify the source.
    if (labelInfo && !isMobile) {
      result.push(
        <span key="youLabel" className={workBoxStyles.labelInfo}>{labelInfo}</span>
      );
    }
    if (!isSidePlayer && !isMobile && isScreenSource) {
      result.push(
        <span key="screen-badge" className={styles.screenBadge}>
          <ReactIcons.ComputerScreen />
          {'Écran'}
        </span>
      );
    }
    return result;
  }, [extraHeaderContent, isHighlighted, isMe, isScreenSource, isSidePlayer, isMobile, label, labelInfo]);

  const right = React.useMemo(() => {
    const result: React.ReactNode[] = [];
    if (video !== undefined && mediaButtons) {
      result.push(
        <PrimaryButton
          key="video"
          onClick={onChangeVideo}
        >
          <CircleIcon.Variants.Camera
            className={styles.mediaBtn}
            disabled={!video}
          />
        </PrimaryButton>
      );
    }
    if (audio !== undefined && mediaButtons) {
      result.push(
        <PrimaryButton
          key="audio"
          onClick={onChangeAudio}
        >
          <CircleIcon.Variants.Mic
            className={styles.mediaBtn}
            disabled={!audio}
          />
        </PrimaryButton>
      );
    }
    topbarActions?.forEach((action, idx) => {
      result.push(
        <PrimaryButton
          key={action.key ?? `topbar-action-${idx}`}
          onClick={action.onClick}
          nativeProps={action.label ? { "aria-label": action.label, title: action.label } : undefined}
        >
          <CircleIcon icon={action.icon} className={styles.mediaBtn} />
        </PrimaryButton>
      );
    });
    return result;
  }, [video, mediaButtons, audio, onChangeVideo, onChangeAudio, topbarActions]);

  const renderDisabedCameraContent = () => {
    if (video) return null;
    return (
      <div className={styles.disabledCameraOverlay}>
        <UserAvatar
          mode="image"
          src={avatar}
          username={userDisplayName}
          className={styles.avatar}
        />
        <span className={styles.disabledCameraLabel}>{'La vidéo est désactivée'}</span>
      </div>
    );
  }

  const renderIdleAnimation = () => {
    if (!idleAnimation) return null;
    return (
      <div className={styles.idleAnimationContainer}>
        <FluidCanvas speed={0.5} shapeSize="big" />
      </div>
    );
  };

  const renderLoadingOverlay = () => {
    if (broken || !loading) return null;
    return (
      <div className={styles.loadingOverlay}>
        <ReactIcons.Spinner className={styles.spinner} />
      </div>
    );
  };

  const renderBrokenOverlay = () => {
    if (!broken) return null;
    return (
      <div className={styles.brokenOverlay}>
        <div className={styles.brokenMessage}>Impossible de lire le flux vidéo</div>
      </div>
    );
  };

  const resolvedOverlayContent: React.ReactNode = (() => {
    if (!overlayContent) return undefined;
    if (!isTypedOverlay(overlayContent)) return overlayContent;
    if (overlayContent.type === "controls") {
      return (
        <VideoControlsOverlay
          onUp={overlayContent.onUp}
          onDown={overlayContent.onDown}
          onLeft={overlayContent.onLeft}
          onRight={overlayContent.onRight}
          onZoomIn={overlayContent.onZoomIn}
          onZoomOut={overlayContent.onZoomOut}
        >
          <></>
        </VideoControlsOverlay>
      );
    }
    if (overlayContent.type === "selection") {
      const Icon = focusIconForLayout(overlayContent.layout);
      return (
        <Touchable onClick={overlayContent.onSelect} className={styles.selection}>
          <Icon style={{ color: "white" }} />
        </Touchable>
      );
    }
    return undefined;
  })();

  const hasPtzControls = isTypedOverlay(overlayContent) && overlayContent.type === "controls";

  /* ----------------------------- Minimap / crop ---------------------------- */
  // crop mode = the minimap feature is enabled AND a pan is set. The crop/pan
  // applies wherever the source renders; the toggle + minimap UI only show on
  // non-strip tiles. In crop mode we let drags reach the viewport (unless PTZ
  // controls need the clicks).
  const cropMode = !!minimap?.enabled && minimap.pan !== undefined;
  const showMinimapUi = !!minimap?.enabled && !isSidePlayer;
  
  // Let drags reach the viewport in crop mode — but not when PTZ controls or a
  // side-player selection overlay need the clicks.
  const overlayClickThrough = cropMode && !hasPtzControls && !isSidePlayer;

  const minimapOnTop = minimap?.position === "top";

  const minimapOverlay = !showMinimapUi ? null : cropMode ? (
    // Inside the provider → reads `canPan` and hides itself when nothing to pan.
    <MinimapCropControls onExit={() => minimap?.onPanChange?.(undefined)} inverted={minimapOnTop} />
  ) : (
    // Full-frame: just the "enter crop" button (no context here).
    <div className={styles.minimapOverlay}>
      <PrimaryButton
        onClick={() => minimap?.onPanChange?.({ x: 0.5, y: 0.5 })}
        nativeProps={{ "aria-label": "Recadrer (mode interactif)", title: "Recadrer (mode interactif)" }}
      >
        <CircleIcon.Variants.UltraWide active={false} className={styles.minimapToggleCircle} />
      </PrimaryButton>
    </div>
  );

  // The toggle + minimap sit at the bottom (or top when `position: "top"`),
  // lifted clear of the PTZ row when controls are present, and rendered last
  // so they stay draggable on top.
  const finalOverlayContent: React.ReactNode = minimapOverlay ? (
    <div className={styles.overlayLayer}>
      {resolvedOverlayContent}
      <div
        className={clsx(
          styles.minimapSlot,
          minimapOnTop ? styles.minimapSlotTop : hasPtzControls && styles.minimapSlotWithControls,
        )}
      >
        {minimapOverlay}
      </div>
    </div>
  ) : resolvedOverlayContent;

  const box = (
    <BoxContainer
      className={clsx({
        [styles.container]: true,
        [styles.highlighted]: isHighlighted,
        [styles.playingSound]: isPlayingSound,
        [styles.small]: isSmall,
        [styles.mobile]: isMobile,
      }, className)}
      topBarProps={{
        left,
        center: [],
        right: right.length ? right : undefined,
        className: clsx({
          [styles.playerTopBar]: true,
          [styles.transparent]: !video,
        }, topBarClassName),
      }}
      mode={overlayMode}
      overlayContent={finalOverlayContent}
      hideOverlayAfterMs={2000}
      overlayClickThrough={overlayClickThrough}
      nativeProps={boxProps}
      containerRef={containerRef}
    >
      {/* In crop mode the player (`children`) is the MinimapCrop content, rendered
          by the viewport (here) and the minimap; otherwise it's rendered directly. */}
      {cropMode ? <MinimapCropViewport overflowMode="x" style={{ width: "100%", height: "100%" }} /> : children}
      {renderDisabedCameraContent()}
      {renderIdleAnimation()}
      {renderLoadingOverlay()}
      {renderBrokenOverlay()}
      {localStream && settings.dev && (
        <div style={{
          position: "absolute",
          bottom: 10,
          left: 10,
          color: "white",
          fontSize: "0.75em",
          backgroundColor: "#00000088",
          padding: "2px 5px",
          borderRadius: 5,
        }}>
          local-stream
        </div>
      )}
    </BoxContainer>
  );

  if (!cropMode) return box;

  return (
    <MinimapCropProvider
      content={children}
      minimapContent={minimap?.minimapContent}
      contentAspectRatio={minimap?.ratio ?? 16 / 9}
      pan={minimap?.pan}
      onPanChange={(p) => minimap?.onPanChange?.(p)}
    >
      {box}
    </MinimapCropProvider>
  );
});
